import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

// Request/Response Types
export interface CreateOfferRequest {
  nftTokenId: string;
  offerType: 'buy' | 'sell';
  amount: string; // XRP amount as string
  currency?: string;
  destination?: string; // Specific buyer/seller address (optional)
  expiresAt?: string; // ISO datetime
  reason?: string;
}

export interface CreateOfferResponse extends ApiResponse<any> {
  offer?: any;
  xamanPayload?: XamanPayloadResponse;
  transactionUuid?: string;
}

export interface AcceptOfferRequest {
  acceptanceType: 'direct' | 'brokered';
}

export interface AcceptOfferResponse extends ApiResponse<any> {
  transaction?: any;
  xamanPayload?: XamanPayloadResponse;
  transactionUuid?: string;
  brokerTransactionId?: string;
}

export interface CancelOfferResponse extends ApiResponse<any> {
  xamanPayload?: XamanPayloadResponse;
  transactionUuid?: string;
}

export interface Offer {
  offer_id: string;
  nft_token_id: string;
  offer_type: 'buy' | 'sell';
  amount: string;
  currency: string;
  owner: string;
  destination?: string;
  expiration?: number;
  status: 'active' | 'accepted' | 'cancelled' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface GetOffersParams {
  nftTokenId?: string;
  offerType?: 'buy' | 'sell';
  status?: string;
  limit?: number;
  offset?: number;
}

export interface GetOffersResponse extends ApiResponse<any> {
  offers?: Offer[];
  total?: number;
  pagination?: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface GetOfferResponse extends ApiResponse<any> {
  offer?: Offer;
}

export interface GetNFTOffersParams {
  offerType?: 'buy' | 'sell';
  status?: string;
  limit?: number;
  offset?: number;
}

export interface GetNFTOffersResponse extends ApiResponse<any> {
  offers?: Offer[];
  marketDepth?: {
    buyOrders: number;
    sellOrders: number;
    highestBid: string;
    lowestAsk: string;
  };
}

export interface GetUserOffersParams {
  status?: string;
  limit?: number;
  offset?: number;
}

export interface GetUserOffersResponse extends ApiResponse<any> {
  offers?: Offer[];
  stats?: {
    totalOffers: number;
    activeOffers: number;
    completedOffers: number;
    totalVolume: string;
  };
}

export interface TradingStats {
  globalStats?: {
    totalOffers: number;
    totalVolume: string;
    activeTraders: number;
    last24hVolume: string;
  };
}

// API Functions

/**
 * Create a new NFT offer (buy or sell)
 */
export async function createOffer(data: CreateOfferRequest): Promise<CreateOfferResponse> {
  try {
    return await apiClient.post<CreateOfferResponse>('/api/v1/offers', data);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create offer',
    };
  }
}

/**
 * Accept an existing offer
 */
export async function acceptOffer(
  offerId: string,
  data: AcceptOfferRequest
): Promise<AcceptOfferResponse> {
  try {
    return await apiClient.post<AcceptOfferResponse>(`/api/v1/offers/${offerId}/accept`, data);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to accept offer',
    };
  }
}

/**
 * Cancel an existing offer
 */
export async function cancelOffer(offerId: string, reason?: string): Promise<CancelOfferResponse> {
  try {
    return await apiClient.post<CancelOfferResponse>(`/api/v1/offers/${offerId}/cancel`, {
      reason,
    });
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to cancel offer',
    };
  }
}

/**
 * Get offers with optional filters
 */
export async function getOffers(params: GetOffersParams = {}): Promise<GetOffersResponse> {
  try {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/offers?${queryString}` : '/api/v1/offers';

    return await apiClient.get<GetOffersResponse>(endpoint);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get offers',
    };
  }
}

/**
 * Get a specific offer by ID
 */
export async function getOffer(offerId: string): Promise<GetOfferResponse> {
  try {
    return await apiClient.get<GetOfferResponse>(`/api/v1/offers/${offerId}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get offer',
    };
  }
}

/**
 * Get all offers for a specific NFT
 */
export async function getNFTOffers(
  nftTokenId: string,
  params: GetNFTOffersParams = {}
): Promise<GetNFTOffersResponse> {
  try {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/v1/offers/nft/${nftTokenId}?${queryString}`
      : `/api/v1/offers/nft/${nftTokenId}`;

    return await apiClient.get<GetNFTOffersResponse>(endpoint);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get NFT offers',
    };
  }
}

/**
 * Get current user's offers
 */
export async function getUserOffers(
  params: GetUserOffersParams = {}
): Promise<GetUserOffersResponse> {
  try {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/offers/my?${queryString}` : '/api/v1/offers/my';

    return await apiClient.get<GetUserOffersResponse>(endpoint);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get user offers',
    };
  }
}

/**
 * Get count of new offers since last viewed (for nav badge)
 */
export async function getNewOfferCount(): Promise<number> {
  const result = await apiClient.get<{ new_offer_count: number }>('/offers/new-count');
  return result.new_offer_count;
}

/**
 * Mark offers as viewed (clears the new offer badge)
 */
export async function markOffersViewed(): Promise<void> {
  await apiClient.post('/offers/mark-viewed', {});
}

/**
 * Get global trading statistics
 */
export async function getTradingStats(): Promise<TradingStats & ApiResponse<any>> {
  try {
    return await apiClient.get<TradingStats & ApiResponse<any>>('/api/v1/offers/stats');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get trading stats',
    };
  }
}
